import { useMemo, useState } from "react";
import { useTeamSelection } from "@/payroll/teams/use-team-selection";
import type {
  PayrollTeam,
  PayrollRosterMember,
  PayrollRosterField,
  PayrollRosterFieldFilters,
} from "./types";
import {
  activeRosterFilterCount,
  buildPayrollRosterFilterFields,
  filterPayrollRosterDrafts,
} from "./utils";

const EMPTY_TEAMS: PayrollTeam[] = [];
const EMPTY_OPTIONS: Record<string, string[]> = {};

/** Shared filter state, choices, and row matching for editable and historical rosters. */
export const useRosterFilters = <T extends { member: PayrollRosterMember }>(
  rows: T[],
  fields: PayrollRosterField[],
  {
    fieldOptions = EMPTY_OPTIONS,
    teams = EMPTY_TEAMS,
    businessId = null,
  }: {
    fieldOptions?: Record<string, string[]>;
    teams?: PayrollTeam[];
    businessId?: string | null;
  } = {},
) => {
  const { teamIds, setTeamIds } = useTeamSelection(businessId, teams);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<PayrollRosterFieldFilters>({});
  const filterFields = useMemo(
    () => buildPayrollRosterFilterFields(rows, fields, fieldOptions),
    [rows, fields, fieldOptions],
  );
  const filteredRows = useMemo(
    () =>
      filterPayrollRosterDrafts(rows, fields, search, filters).filter(
        ({ member }) =>
          teamIds.length === 0 ||
          member.team_access_key_ids?.some((id) => teamIds.includes(id)),
      ),
    [rows, fields, search, filters, teamIds],
  );
  const activeFilterCount =
    activeRosterFilterCount(filters) + (teamIds.length ? 1 : 0);
  const clearFilters = () => {
    setTeamIds([]);
    setSearch("");
    setFilters({});
  };
  return {
    filteredRows,
    search,
    activeFilterCount,
    hasActiveFilters: search.trim().length > 0 || activeFilterCount > 0,
    filterKey: `${search.trim().toLocaleLowerCase()}-${JSON.stringify(filters)}-${teamIds.join(",")}`,
    clearFilters,
    controls: {
      teams,
      teamIds,
      onTeamIdsChange: setTeamIds,
      search,
      filters,
      filterFields,
      onSearchChange: setSearch,
      onFiltersChange: setFilters,
    },
  };
};
